import React from 'react';
import {
    Form, Input, Row, Col, Upload, Space, Typography, ConfigProvider, Tooltip
} from 'antd';
import {
    ScanOutlined, PaperClipOutlined, DeleteOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PrescriptionFields from './PrescriptionFields';
import SuggestingTextArea from './SuggestingTextArea';
import VisitLabTestCards from './VisitLabTestCards';
import { EYES, EXAM_COLUMNS, POWER_COLUMNS } from '../utils/glasses';
import ToothChart from './Dental/ToothChart';
import './VisitFormFields.css';

const { Text } = Typography;

// The body of the visit form, shared by "create visit" and "edit visit".
//
// One copy, for two reasons. The obvious one is that two copies drift - the edit
// form used to render every field unconditionally, so a doctor who had switched
// LMP off still met it the moment they corrected a typo. The other is that the
// layout below only works if it is maintained once: everything is sized and
// grouped to fit a screen without scrolling, and that is not a property that
// survives being reimplemented.
//
// Density is deliberate. A consultation is interrupted work - the doctor is
// looking at the patient, not the screen - so the whole form has to be readable
// at a glance. Controls are small, sections are three or four to a row, and text
// areas are two rows deep and grow as they are typed into.

// The upload box next to an imaging findings field. Films for a modality are
// kept with that modality, so "what did the CT show?" is answered in one place.
//
// `existing` are films already stored on the visit (edit mode); `fileList` are
// ones picked in this session, which the parent uploads once the visit has an id.
const ImagingUpload = ({ label, existing = [], fileList, onChange, onRemoveExisting, disabled }) => {
    const { t } = useTranslation();

    return (
        <div className="visit-imaging-upload">
            {existing.length > 0 && (
                <Space size={4} wrap style={{ marginBottom: 4 }}>
                    {existing.map((file) => (
                        <span key={file._id} className="visit-imaging-chip">
                            <a href={file.fileUrl} target="_blank" rel="noopener noreferrer">
                                <PaperClipOutlined /> {file.fileName || t('file')}
                            </a>
                            {onRemoveExisting && (
                                <Tooltip title={t('delete')}>
                                    <DeleteOutlined
                                        className="visit-imaging-chip-remove"
                                        onClick={() => onRemoveExisting(file)}
                                    />
                                </Tooltip>
                            )}
                        </span>
                    ))}
                </Space>
            )}
            <Upload
                listType="picture-card"
                fileList={fileList}
                onChange={({ fileList: next }) => onChange(next)}
                beforeUpload={() => false}
                accept="image/*,.pdf"
                multiple
                disabled={disabled}
                className="visit-imaging-uploader"
            >
                {fileList.length >= 10 ? null : (
                    <div className="visit-imaging-add">
                        <ScanOutlined />
                        <div>{t('addFilm') || 'Film'}</div>
                    </div>
                )}
            </Upload>
            <Text type="secondary" className="visit-hint">
                {t('attachFilmHint', { modality: label }) || `Attach the ${label} image or report`}
            </Text>
        </div>
    );
};

const VisitFormFields = ({
    form,
    showField,
    drugs = [],
    visitSuggestions = { complaints: [], diagnoses: [] },
    showObGynVitals = false,
    // The lab-test catalog (GET /api/lab-tests): what "Add Lab Test" offers,
    // each test with the boxes it opens.
    labCatalog = [],
    labCategories = [],
    // 'male' | 'female' | undefined - which reference range the lab boxes use.
    patientSex,
    // General visit attachments, staged until the visit exists.
    attachmentList = [],
    onAttachmentListChange,
    // Imaging films, staged the same way.
    xrayFileList = [],
    onXrayFileListChange,
    ctScanFileList = [],
    onCtScanFileListChange,
    ultrasoundFileList = [],
    onUltrasoundFileListChange,
    existingXrayFiles = [],
    existingCtScanFiles = [],
    existingUltrasoundFiles = [],
    onRemoveExistingImaging,
    // The edit form carries a visit date; the create form dates the visit now.
    showVisitDate = false
}) => {
    const { t } = useTranslation();

    const historyFields = ['gpm', 'lmp', 'edd', 'dngh', 'psh', 'pmh'].filter(showField);
    const showImaging = showField('xray') || showField('ctScan')
        || showField('pelvicUs') || showField('obstetricUs');

    const sectionTitle = (text) => <div className="visit-section-title">{text}</div>;

    return (
        <ConfigProvider componentSize="small">
            <div className="visit-form-dense">
                {showVisitDate && (
                    <Row gutter={8}>
                        <Col xs={24} md={8}>
                            <Form.Item name="visitDate" label={t('visitDate')}>
                                <Input type="datetime-local" />
                            </Form.Item>
                        </Col>
                    </Row>
                )}

                {/* The three boxes a doctor fills in at almost every visit, side by
                    side rather than stacked - stacked, they alone filled a screen. */}
                <Row gutter={8}>
                    {showField('complaints') && (
                        <Col xs={24} md={8}>
                            <Form.Item name="complaints" label={t('complaints')}>
                                <SuggestingTextArea
                                    autoSize={{ minRows: 2, maxRows: 6 }}
                                    suggestions={visitSuggestions.complaints}
                                    placeholder={t('complaints')}
                                />
                            </Form.Item>
                        </Col>
                    )}
                    {showField('diagnosis') && (
                        <Col xs={24} md={8}>
                            <Form.Item name="diagnosis" label={t('diagnosis')}>
                                <SuggestingTextArea
                                    autoSize={{ minRows: 2, maxRows: 6 }}
                                    suggestions={visitSuggestions.diagnoses}
                                    placeholder={t('diagnosis')}
                                />
                            </Form.Item>
                        </Col>
                    )}
                    {showField('notes') && (
                        <Col xs={24} md={8}>
                            <Form.Item name="notes" label={t('notes')}>
                                <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} placeholder={t('notes')} />
                            </Form.Item>
                        </Col>
                    )}
                </Row>

                {historyFields.length > 0 && (
                    <>
                        {sectionTitle(t('patientHistory') || 'Patient History')}
                        <Row gutter={8}>
                            {showField('gpm') && (
                                <Col xs={12} md={4}>
                                    <Form.Item name="gpm" label={t('gpm')} tooltip={t('gpmFull')}>
                                        <Input placeholder="G3 P2 M0" />
                                    </Form.Item>
                                </Col>
                            )}
                            {showField('lmp') && (
                                <Col xs={12} md={4}>
                                    <Form.Item name="lmp" label={t('lmp')} tooltip={t('lmpFull')}>
                                        <Input type="date" />
                                    </Form.Item>
                                </Col>
                            )}
                            {showField('edd') && (
                                <Col xs={12} md={4}>
                                    <Form.Item
                                        name="edd"
                                        label={t('edd')}
                                        tooltip={showField('lmp') ? t('eddFromLmp') : t('eddFull')}
                                    >
                                        <Input type="date" />
                                    </Form.Item>
                                </Col>
                            )}
                            {showField('dngh') && (
                                <Col xs={12} md={4}>
                                    <Form.Item name="dngh" label={t('dngh')} tooltip={t('dnghFull')}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                            )}
                            {showField('psh') && (
                                <Col xs={12} md={4}>
                                    <Form.Item name="psh" label={t('psh')} tooltip={t('pshFull')}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                            )}
                            {showField('pmh') && (
                                <Col xs={12} md={4}>
                                    <Form.Item name="pmh" label={t('pmh')} tooltip={t('pmhFull')}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                            )}
                        </Row>
                    </>
                )}

                {showField('onExamination') && (
                    <>
                        {sectionTitle(t('onExamination') || 'On Examination')}
                        <Form.Item name="onExamination">
                            <Input.TextArea
                                autoSize={{ minRows: 2, maxRows: 6 }}
                                placeholder={t('onExaminationFindings') || 'Examination findings'}
                            />
                        </Form.Item>
                    </>
                )}

                {showField('vitalSigns') && (
                    <>
                        {sectionTitle(t('vitalSigns'))}
                        <Row gutter={8}>
                            <Col xs={8} md={4}>
                                <Form.Item name={['vitalSigns', 'bloodPressure']} label={t('bloodPressure')}>
                                    <Input placeholder="120/80" />
                                </Form.Item>
                            </Col>
                            <Col xs={8} md={4}>
                                <Form.Item name={['vitalSigns', 'temperature']} label={t('temperature')}>
                                    <Input placeholder="37" />
                                </Form.Item>
                            </Col>
                            <Col xs={8} md={4}>
                                <Form.Item name={['vitalSigns', 'pulse']} label={t('pulse')}>
                                    <Input placeholder="72" />
                                </Form.Item>
                            </Col>
                            <Col xs={8} md={4}>
                                <Form.Item name={['vitalSigns', 'weight']} label={`${t('weight')} (kg)`}>
                                    <Input placeholder="70" />
                                </Form.Item>
                            </Col>
                            {showObGynVitals && (
                                <Col xs={8} md={4}>
                                    <Form.Item name={['vitalSigns', 'height']} label={`${t('height')} (cm)`}>
                                        <Input placeholder="165" />
                                    </Form.Item>
                                </Col>
                            )}
                            {showObGynVitals && (
                                <Col xs={8} md={4}>
                                    <Form.Item
                                        name={['vitalSigns', 'bmi']}
                                        label={t('bmi') || 'BMI'}
                                        tooltip={t('bmiAutoCalculated')}
                                    >
                                        <Input placeholder="auto" />
                                    </Form.Item>
                                </Col>
                            )}
                        </Row>
                    </>
                )}

                {showImaging && (
                    <>
                        {sectionTitle(t('imaging'))}
                        <Row gutter={8}>
                            {showField('xray') && (
                                <Col xs={24} md={12}>
                                    <Form.Item name="xray" label={t('xray')} className="visit-item-tight">
                                        <Input.TextArea
                                            autoSize={{ minRows: 2, maxRows: 5 }}
                                            placeholder={t('xrayFindings')}
                                        />
                                    </Form.Item>
                                    <ImagingUpload
                                        label={t('xray')}
                                        existing={existingXrayFiles}
                                        fileList={xrayFileList}
                                        onChange={onXrayFileListChange}
                                        onRemoveExisting={
                                            onRemoveExistingImaging
                                                ? (file) => onRemoveExistingImaging('xray', file)
                                                : undefined
                                        }
                                    />
                                </Col>
                            )}
                            {showField('ctScan') && (
                                <Col xs={24} md={12}>
                                    <Form.Item name="ctScan" label={t('ctScan')} className="visit-item-tight">
                                        <Input.TextArea
                                            autoSize={{ minRows: 2, maxRows: 5 }}
                                            placeholder={t('ctScanFindings')}
                                        />
                                    </Form.Item>
                                    <ImagingUpload
                                        label={t('ctScan')}
                                        existing={existingCtScanFiles}
                                        fileList={ctScanFileList}
                                        onChange={onCtScanFileListChange}
                                        onRemoveExisting={
                                            onRemoveExistingImaging
                                                ? (file) => onRemoveExistingImaging('ctScan', file)
                                                : undefined
                                        }
                                    />
                                </Col>
                            )}
                            {showField('pelvicUs') && (
                                <Col xs={24} md={12}>
                                    <Form.Item name="pelvicUs" label={t('pelvicUs') || 'Pelvic US'}>
                                        <Input.TextArea
                                            autoSize={{ minRows: 2, maxRows: 5 }}
                                            placeholder={t('pelvicUsFindings') || 'Pelvic ultrasound findings'}
                                        />
                                    </Form.Item>
                                </Col>
                            )}
                            {showField('obstetricUs') && (
                                <Col xs={24} md={12}>
                                    <Form.Item name="obstetricUs" label={t('obstetricUs') || 'Obstetric US'}>
                                        <Input.TextArea
                                            autoSize={{ minRows: 2, maxRows: 5 }}
                                            placeholder={t('obstetricUsFindings') || 'Obstetric ultrasound findings'}
                                        />
                                    </Form.Item>
                                </Col>
                            )}
                        </Row>
                    </>
                )}

                {showField('ultrasound') && (
                    <>
                        {sectionTitle(t('ultrasound') || 'Ultrasound')}
                        {/* One line per organ, as the urology report reads and as
                            the doctor's previous system laid it out; the images go
                            underneath the lines they illustrate. */}
                        <Row gutter={8}>
                            {[
                                ['rightKidney', t('rightKidney') || 'Right Kidney'],
                                ['leftKidney', t('leftKidney') || 'Left Kidney'],
                                ['bladder', t('bladder') || 'Bladder'],
                                ['prostate', t('prostate') || 'Prostate'],
                                ['pvru', t('pvru') || 'PVRU']
                            ].map(([key, label]) => (
                                <Col xs={12} md={4} key={key}>
                                    <Form.Item name={['ultrasound', key]} label={label}>
                                        <Input placeholder={label} />
                                    </Form.Item>
                                </Col>
                            ))}
                            <Col xs={24} md={4}>
                                <Form.Item name={['ultrasound', 'note']} label={t('notes')}>
                                    <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} placeholder={t('notes')} />
                                </Form.Item>
                            </Col>
                        </Row>
                        {onUltrasoundFileListChange && (
                            <ImagingUpload
                                label={t('ultrasound') || 'Ultrasound'}
                                existing={existingUltrasoundFiles}
                                fileList={ultrasoundFileList}
                                onChange={onUltrasoundFileListChange}
                                onRemoveExisting={
                                    onRemoveExistingImaging
                                        ? (file) => onRemoveExistingImaging('ultrasound', file)
                                        : undefined
                                }
                            />
                        )}
                    </>
                )}

                {showField('dental') && (
                    <>
                        {sectionTitle(t('dental'))}
                        {/* The chart is a form field like any other: what is
                            marked here is saved with the visit. */}
                        <Form.Item name={['dental', 'chart']} className="visit-item-tight">
                            <ToothChart />
                        </Form.Item>
                        <Row gutter={8}>
                            <Col xs={24} md={4}>
                                <Form.Item name={['dental', 'teeth']} label={t('dentalTeeth')} tooltip={t('dentalTeethHint')}>
                                    <Input placeholder="16, 26" />
                                </Form.Item>
                            </Col>
                            {[
                                ['findings', 'dentalFindings'],
                                ['procedure', 'dentalProcedure'],
                                ['treatmentPlan', 'dentalTreatmentPlan']
                            ].map(([key, labelKey]) => (
                                <Col xs={24} md={key === 'findings' ? 8 : 6} key={key}>
                                    <Form.Item name={['dental', key]} label={t(labelKey)}>
                                        <Input.TextArea autoSize={{ minRows: 1, maxRows: 5 }} placeholder={t(labelKey)} />
                                    </Form.Item>
                                </Col>
                            ))}
                        </Row>
                    </>
                )}

                {showField('eyeExam') && (
                    <>
                        {sectionTitle(t('eyeExam'))}
                        {/* One line per eye, right first, as the examination is
                            written down. */}
                        {EYES.map(([eye, labelKey]) => (
                            <Row gutter={8} key={eye}>
                                <Col xs={24} md={4} className="visit-eye-label">{t(labelKey)}</Col>
                                {EXAM_COLUMNS.map(([suffix, columnKey, , placeholder]) => (
                                    <Col xs={8} md={4} key={suffix}>
                                        <Form.Item name={['eyeExam', `${eye}${suffix}`]} label={t(columnKey)}>
                                            <Input placeholder={placeholder} />
                                        </Form.Item>
                                    </Col>
                                ))}
                            </Row>
                        ))}
                        <Row gutter={8}>
                            <Col xs={24} md={12}>
                                <Form.Item name={['eyeExam', 'anteriorSegment']} label={t('anteriorSegment')}>
                                    <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} placeholder={t('anteriorSegment')} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item name={['eyeExam', 'fundus']} label={t('fundus')}>
                                    <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} placeholder={t('fundus')} />
                                </Form.Item>
                            </Col>
                        </Row>
                    </>
                )}

                {showField('glasses') && (
                    <>
                        {sectionTitle(t('glassesPrescription'))}
                        {/* What goes to the optician. The powers are typed left to
                            right in every language, sign first. */}
                        {EYES.map(([eye, labelKey]) => (
                            <Row gutter={8} key={eye}>
                                <Col xs={24} md={4} className="visit-eye-label">{t(labelKey)}</Col>
                                {POWER_COLUMNS.map(([suffix, label, placeholder]) => (
                                    <Col xs={6} md={4} key={suffix}>
                                        <Form.Item name={['glasses', `${eye}${suffix}`]} label={label}>
                                            <Input placeholder={placeholder} dir="ltr" />
                                        </Form.Item>
                                    </Col>
                                ))}
                            </Row>
                        ))}
                        <Row gutter={8}>
                            <Col xs={8} md={4}>
                                <Form.Item name={['glasses', 'pd']} label={t('pupillaryDistance')}>
                                    <Input placeholder="62" dir="ltr" />
                                </Form.Item>
                            </Col>
                            <Col xs={16} md={8}>
                                <Form.Item name={['glasses', 'lensType']} label={t('lensType')}>
                                    <Input placeholder={t('lensTypeHint')} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item name={['glasses', 'note']} label={t('notes')}>
                                    <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} placeholder={t('glassesNoteHint')} />
                                </Form.Item>
                            </Col>
                        </Row>
                    </>
                )}

                {showField('prescriptions') && (
                    <>
                        {sectionTitle(t('prescriptions'))}
                        <PrescriptionFields form={form} drugs={drugs} />
                    </>
                )}

                {showField('labTests') && (
                    <>
                        {sectionTitle(t('labTests'))}
                        {/* Pick a test, press Add, fill in its boxes. The boxes come
                            from the catalog, so what a CBC is made of is decided in
                            the admin screen, not here. */}
                        <VisitLabTestCards form={form} catalog={labCatalog} categories={labCategories} patientSex={patientSex} allowOrder />
                    </>
                )}

                <Row gutter={8} className="visit-footer-row">
                    {showField('attachments') && onAttachmentListChange && (
                        <Col xs={24} md={showField('nextVisitDate') ? 16 : 24}>
                            <div className="visit-section-title">{t('attachments')}</div>
                            <Upload
                                listType="picture-card"
                                fileList={attachmentList}
                                onChange={({ fileList: next }) => onAttachmentListChange(next)}
                                beforeUpload={() => false}
                                accept="image/*,.pdf,.doc,.docx"
                                multiple
                                capture="environment"
                                className="visit-imaging-uploader"
                            >
                                {attachmentList.length >= 20 ? null : (
                                    <div className="visit-imaging-add">
                                        <ScanOutlined />
                                        <div>{t('scanUpload') || 'Scan'}</div>
                                    </div>
                                )}
                            </Upload>
                        </Col>
                    )}
                    {showField('nextVisitDate') && (
                        <Col xs={24} md={8}>
                            <div className="visit-section-title">{t('nextVisitDate')}</div>
                            <Form.Item name="nextVisitDate">
                                <Input type="datetime-local" />
                            </Form.Item>
                        </Col>
                    )}
                </Row>
            </div>
        </ConfigProvider>
    );
};

export default VisitFormFields;
